import React, { useEffect } from "react";
import { Mail, AlertTriangle, FileText, Shield } from "lucide-react";
import GlobalFooter from "../components/GlobalFooter";
import PublicLegalDocument from "../components/PublicLegalDocument";

export default function Takedown() {
  useEffect(() => {
    // Hide Emergent copilot on public Takedown page
    const copilotWidget = document.querySelector('#emergent-copilot, [data-emergent-copilot], .emergent-chat-widget');
    if (copilotWidget) {
      copilotWidget.style.display = 'none';
    }
    
    return () => {
      if (copilotWidget) {
        copilotWidget.style.display = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0A0A0E] to-black text-white flex flex-col">
      <div className="container mx-auto px-6 py-12 max-w-4xl flex-1">
        {/* Header */}
        <div className="mb-12 border-b border-white/10 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30">
              <AlertTriangle size={32} className="text-red-400" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              DMCA Takedown & Content Removal
            </h1>
          </div>
          <p className="text-zinc-500">Content Removal Requests & Copyright Claims</p>
        </div>

        {/* Content */}
        <PublicLegalDocument docType="takedown">
        <div className="space-y-8 prose prose-invert max-w-none">
          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Overview</h2>
            <p className="text-zinc-300 leading-relaxed">
              We respect the intellectual property rights of others and expect our users to do the same. 
              If you believe that content hosted on our platform infringes your copyright or other 
              intellectual property rights, we provide a clear process for submitting takedown requests 
              in compliance with the Digital Millennium Copyright Act (DMCA).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">When to File a Takedown Request</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You may submit a takedown request if:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Content on our platform infringes your copyright (images, videos, text, etc.)</li>
              <li>Your likeness or image is used without your consent</li>
              <li>Content violates your privacy or personal rights</li>
              <li>Content contains non-consensual intimate material</li>
              <li>You are the model/performer and wish to remove your content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">DMCA Takedown Notice Requirements</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              To submit a valid DMCA takedown notice, please include the following information:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li><strong>Your contact information:</strong> Full name, email address, phone number, and physical address</li>
              <li><strong>Identification of copyrighted work:</strong> Detailed description of the work you claim is infringed</li>
              <li><strong>Identification of infringing material:</strong> Direct URL(s) to the content on our platform</li>
              <li><strong>Statement of good faith:</strong> A statement that you believe in good faith the use is not authorized</li>
              <li><strong>Statement of accuracy:</strong> A statement that the information is accurate and you are the copyright owner or authorized to act</li>
              <li><strong>Physical or electronic signature:</strong> Your typed full name or digital signature</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">How to Submit a Takedown Request</h2>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <p className="text-zinc-300 mb-4">
                Send your DMCA takedown notice or content removal request to:
              </p>
              <a 
                href="mailto:privacy@giopilos.com"
                className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 transition font-semibold text-lg"
              >
                <Mail size={20} />
                privacy@giopilos.com
              </a>
              <p className="text-zinc-400 text-sm mt-4">
                <strong>Email Subject Line:</strong> &quot;DMCA Takedown Request - [Your Name]&quot;
              </p>
              <p className="text-zinc-400 text-sm mt-2">
                Please include all required information listed above. Incomplete requests may delay processing.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Processing Timeline</h2>
            <div className="bg-zinc-900 border border-emerald-900/30 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Shield size={20} className="text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-emerald-400 font-semibold mb-2">We take content removal seriously.</p>
                  <p className="text-zinc-300 leading-relaxed">
                    Valid DMCA takedown requests are processed within <strong>24-48 hours</strong> of receipt. 
                    Urgent requests involving non-consensual content or minors are prioritized and addressed 
                    immediately upon verification.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">What Happens After Submission</h2>
            <ol className="list-decimal list-inside text-zinc-300 space-y-3 ml-4">
              <li><strong>Acknowledgment:</strong> We will acknowledge receipt of your request within 24 hours</li>
              <li><strong>Review:</strong> Our team reviews the request for validity and completeness</li>
              <li><strong>Action:</strong> If valid, the content is removed or access is disabled</li>
              <li><strong>Notification:</strong> You will receive confirmation once action is taken</li>
              <li><strong>Counter-notice period:</strong> The uploader may submit a counter-notice (DMCA procedure)</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">False Claims & Abuse</h2>
            <p className="text-zinc-300 leading-relaxed">
              Submitting false DMCA claims or abusing the takedown process is a violation of federal law 
              and may result in legal liability. Under Section 512(f) of the DMCA, anyone who knowingly 
              materially misrepresents that content is infringing may be subject to damages, including 
              costs and attorney fees.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Counter-Notification Process</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If your content was removed due to a DMCA claim and you believe it was removed in error, 
              you may submit a counter-notification. Your counter-notice must include:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Your contact information and physical address</li>
              <li>Identification of the removed content and its previous location</li>
              <li>A statement under penalty of perjury that the content was removed by mistake</li>
              <li>Consent to jurisdiction of federal court in your district</li>
              <li>Your physical or electronic signature</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Repeat Infringer Policy</h2>
            <p className="text-zinc-300 leading-relaxed">
              We maintain a policy of terminating accounts of users who are repeat copyright infringers. 
              Users who receive multiple valid DMCA takedown notices may have their accounts permanently 
              disabled without refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Additional Resources</h2>
            <div className="space-y-3">
              <a 
                href="/privacy"
                className="flex items-center gap-2 text-blue-500 hover:text-blue-400 transition"
              >
                <FileText size={16} />
                Privacy Policy
              </a>
              <a 
                href="/terms"
                className="flex items-center gap-2 text-blue-500 hover:text-blue-400 transition"
              >
                <FileText size={16} />
                Terms of Service
              </a>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-4">Contact Information</h2>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <p className="text-zinc-300 mb-4">
                For all content removal requests, DMCA notices, privacy concerns, or questions:
              </p>
              <a 
                href="mailto:privacy@giopilos.com"
                className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition font-semibold text-lg"
              >
                <Mail size={20} />
                privacy@giopilos.com
              </a>
              <p className="text-zinc-500 text-sm mt-4">
                We are committed to protecting intellectual property rights and responding promptly to 
                all valid takedown requests.
              </p>
            </div>
          </section>
        </div>
        </PublicLegalDocument>
      </div>
      <GlobalFooter />
    </div>
  );
}
